import React, { useEffect, useRef, useState } from 'react';
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import API_URL from '../../config/api';
import { COUNTRY_DIAL_CODES } from '../../data/countryDialCodes';
import './Contact.css';

const SUCCESS_MESSAGE = "Thank you! Your message has been sent successfully. We'll get back to you soon.";
const ERROR_MESSAGE = 'Sorry, there was an error sending your message. Please try again.';
const DEFAULT_COUNTRY_CODE = 'IN';
const DEFAULT_DIAL_CODE = '+91';

const getDialCodeByCountry = (countryCode) =>
  COUNTRY_DIAL_CODES.find((country) => country.code === countryCode)?.dialCode || DEFAULT_DIAL_CODE;

const Contact = () => {
  const { hash } = useLocation();
  const inquiryFormRef = useRef(null);
  const mapSectionRef = useRef(null);
  const meetUsSectionRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: DEFAULT_COUNTRY_CODE,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    const sectionMap = {
      '#map-section': mapSectionRef.current,
      '#meet-us': meetUsSectionRef.current,
      '#inquiry-form': inquiryFormRef.current,
    };

    const targetSection = sectionMap[hash];

    if (targetSection) {
      targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash]);

  const contactInfo = {
    phone: '+91 9167633933',
    email: 'sales@hkinds.com',
    address: 'A-1001, Viceroy Park, Opp RTO office, Kandarpada, Dahisar-West, Mumbai-400068'
  };

  const handleEmailClick = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const handlePhoneClick = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage('');

    const payload = {
      name: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() ? `${getDialCodeByCountry(formData.countryCode)} ${formData.phone.trim()}` : '',
      message: formData.message.trim(),
      type: 'general'
    };

    try {
      const response = await fetch(API_URL.ENQUIRIES, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        const firstError = result?.errors
          ? Object.values(result.errors).flat().find(Boolean)
          : null;
        throw new Error(firstError || result?.message || `HTTP error! status: ${response.status}`);
      }

      setSubmitStatus('success');
      setSubmitMessage(result.message || SUCCESS_MESSAGE);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: DEFAULT_COUNTRY_CODE,
        message: ''
      });
      console.log('Contact form submitted successfully:', result.data);
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage(error.message || ERROR_MESSAGE);
      console.error('Contact form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-container">
        <div className="contact-page-header">
          <h1 className="contact-page-title">Contact Us</h1>
          <div className="contact-page-title-line"></div>
        </div>

        <div id="map-section" ref={mapSectionRef} className="map-section">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.5!2d72.8777!3d19.0760!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTnCsDA0JzM3LjYiTiA3MsKwNTInNDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="HKI Industries Location"
            className="google-map-iframe"
          ></iframe>
        </div>

        <div id="meet-us" ref={meetUsSectionRef} className="contact-info-section">
          <div className="section-title-wrap">
            <h2 className="section-title section-title-blue">Meet Us</h2>
            <div className="section-title-accent"></div>
          </div>
          <div className="contact-details">
            <div className="contact-item">
              <div className="contact-icon">
                <FaPhoneAlt />
              </div>
              <a
                href={`tel:${contactInfo.phone}`}
                onClick={() => handlePhoneClick(contactInfo.phone)}
                className="contact-link"
              >
                {contactInfo.phone}
              </a>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <FaEnvelope />
              </div>
              <a
                href={`mailto:${contactInfo.email}`}
                onClick={() => handleEmailClick(contactInfo.email)}
                className="contact-link"
              >
                {contactInfo.email}
              </a>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <FaMapMarkerAlt />
              </div>
              <span className="contact-text">{contactInfo.address}</span>
            </div>
          </div>
        </div>

        <div id="inquiry-form" ref={inquiryFormRef} className="contact-form-section">
          <div className="section-title-wrap">
            <h2 className="enquiry-form-title enquiry-form-title-blue">Customer Enquiry Form</h2>
            <div className="section-title-accent"></div>
          </div>
          <form className="contact-form enquiry-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Name <span className="required-asterisk">*</span></label>
              <div className="name-row">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Email <span className="required-asterisk">*</span></label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone</label>
              <div className="phone-row">
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  className="form-select country-code"
                >
                  {COUNTRY_DIAL_CODES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.dialCode})
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input phone-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea
                name="message"
                placeholder="Your message or enquiry"
                rows="4"
                value={formData.message}
                onChange={handleInputChange}
                className="form-textarea"
              ></textarea>
            </div>

            {submitStatus === 'success' && (
              <div className="success-message">
                {submitMessage || SUCCESS_MESSAGE}
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="error-message">
                {submitMessage || ERROR_MESSAGE}
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
